import { injectable } from 'inversify';
import { Maps } from '../../../../CodesForFoundation';
import { UniversalSurfaceRenderer } from '../../../../platform/UniversalSurfaceRenderer';
import { Line } from '../../../../platform/geom/Line';
import { Matrix } from '../../../../platform/geom/Matrix';
import { Point } from '../../../../platform/geom/Point';
import { Polyline } from '../../../../platform/geom/Polyline';
import { Rect } from '../../../../platform/geom/Rect';
import { Colors } from '../../../../ui';
import { resolve } from '../../../Container';
import { AutoLayout } from '../../../packages/autoLayout/AutoLayout';
import { AutoLayoutEntry } from '../../../packages/autoLayout/AutoLayoutAssocs';
import { AutoLayoutEngine } from '../../../packages/autoLayout/AutoLayoutEngine';
import { AutoLayoutPotentialInsert } from '../../../packages/autoLayout/LayoutStrategy';
import { Camera } from '../../Camera';
import { EditorSession } from '../../EditorSession';
import { GridSystem } from '../../GridSystem';
import { Selection } from '../../Selection';
import { bringToFront, place, swallow } from '../../commands/OrderCommands';
import { deleteSelection } from '../../commands/SelectionCommands';
import { AutoBringToFront } from '../../entities/AutoBringToFront';
import { DropInfo, DropTarget } from '../../entities/DropTarget';
import { CreateDuplicateInfo, DuplicateInfo } from '../../entities/DuplicateInfo';
import { HasCousins } from '../../entities/HasCousins';
import { Phys } from '../../entities/Phys';
import { Figure, GuideLine, Physical, RelativeTo, TransformScope } from '../../entities/Physical';
import { Physicals } from '../../entities/Physicals';
import { ContentInjector } from '../../injection/ContentInjector';
import { DeqoData } from '../../injection/DeqoData';
import { SpatialIndex } from '../../utilities/SpatialIndex';
import { ToolOverlay } from '../ToolOverlay';
import { SelectionTransformation, Snap } from './SelectionTransformation';
import { TransformationInput } from './Transformation';

@injectable()
export class SelectionTranslateTransformation extends SelectionTransformation {

    public readonly type = 'translate';

    @resolve(AutoLayoutEngine) private autoLayout!: AutoLayoutEngine;
    @resolve(Camera) private camera: Camera;
    @resolve(ContentInjector) private injector: ContentInjector;
    @resolve(EditorSession) private session: EditorSession;
    @resolve(GridSystem) private grid: GridSystem;
    @resolve(SpatialIndex) private spatial: SpatialIndex;
    @resolve(ToolOverlay) private overlay: ToolOverlay;

    private cloning: boolean = false;
    private gridOffset: Point = Point.zero;
    private potentialDrop = null as DropInfo;
    private potentialInsert = null as AutoLayoutPotentialInsert;
    private snapping: boolean = false;
    private snaps: Snap[] = [];

    private anchors = {
        base: null as Point,
        clone: null as Point,
    };

    private snapshots = {
        base: new Map<Physical, Figure>(),
        reset: new Map<Physical, Figure>(),
        clone: new Map<Physical, Figure>(),
    };

    private subjects: Physical[] = [];
    private clones: Physical[] = [];

    protected initialize(): void {

        this.burden(
            this.session.observe(Selection, () => {
                this.rebase();
                // this.invalidateHangingSubjects();
            }),
            this.overlay.add(this.paint.bind(this)),
        );

        this.rebase();

        // If we are translating exclusively objects that auto rise, bring them to the front
        if (Physicals.all(this.subjects, AutoBringToFront)) {
            this.update(() => this.dispatch(bringToFront, [this.subjects.map(x => x.id), true]));
        }
    }

    protected apply(): void {
        const shouldProject = this.latest.modifiers.shift;
        const shouldClone = this.latest.modifiers.alt;

        if (shouldClone) {
            this.startCloning();
        }
        else {
            this.stopCloning();
        }

        const targetName = this.cloning ? 'clone' : 'base';

        const anchor = this.anchors[targetName];
        const latest = this.latest.position;

        let vector = latest.subtract(anchor);

        if (shouldProject) {
            const eighth = Math.PI / 4;
            const angle = Math.abs(vector.angle());
            if (angle <= eighth || angle > Math.PI - eighth) {
                vector = vector.project(Point.x);
            }
            else {
                vector = vector.project(Point.y);
            }
        }

        if (this.snapping) {

            if (this.grid.gridSnap) {
                vector = this.grid.snap(vector);
            }

            if (this.grid.guideSnap) {
                // Maps shape being dragged to its vertices
                const target = this.snapshots[targetName] as Map<Physical, Figure>;

                const transformSoFar = Matrix.identity.translate(vector).translate(this.gridOffset);
                const bounds = Rect.fromPoints(
                    Array.from(target.values()).flatMap(x => transformSoFar.apply(x))
                );

                // In translation, we want to snap to edges and center lines of bounds, so use rect based guidelines
                const guides = GuideLine.fromRect(bounds);
                this.snaps = this.querySnapsFor(guides);

                // Just need to apply ONE of the snaps for X, and ONE for and Y.  Because; if multiple snaps are applicable we
                // return all of them for rendering, but applying all would result in multiple translations
                const snapX = this.snaps.find(s => s.guide.axis === 'x');
                const snapY = this.snaps.find(s => s.guide.axis === 'y');
                vector = vector
                    .add(snapX?.vector ?? Point.zero)
                    .add(snapY?.vector ?? Point.zero);
            }
        }

        const transform = Matrix.identity.translate(vector).translate(this.gridOffset);
        this.update(() => {
            // Just take the heads for the layout / drop
            const elmts = Phys.from(this.applyTransform(targetName, transform)).takeHeads().toArray();
            this.updateAutoLayout(this.latest, elmts);
            this.updatePotentialDrop(this.latest, elmts);
            // this.emit('apply', [this.latest]);

        });

        this.overlay.repaint();
    }

    protected commit(): void {
        this.update(u => {

            const elmts = (this.cloning ? this.clones : this.subjects);
            const ids = elmts.map(x => x.id);

            const didDrop = this.applyPotentialDrop(elmts);
            const didInsert = this.applyAutoLayout(elmts.filter(x => x.alive));

            // Only place/swallow if we didn't do a drop/insert
            if (!didDrop && !didInsert) {
                this.dispatch(place, [ids]);
                this.dispatch(swallow, [ids]);
            }

            this.objects.getActiveTriggerCycle()
                .emit('@manipulated', ids, ['translate']);

            // Use noop just to be sure changes were made
            u.noop();
        });
        if (this.cloning) {
            const values = Object.fromEntries(this.clones.map((c, i) => [c.id, this.subjects[i].id]));
            this.session.update(DuplicateInfo, CreateDuplicateInfo('default', values));
        }
    }

    protected rebase(): void {
        if (this.cloning) return;

        this.subjects = Physicals.topToBottom(Physicals.top(this.session.get(Selection)));
        const lookup = new Set(this.subjects);

        // If we have any subjects with cousins, expand the set to include their cousins.
        for (const ps of Physicals.polarize(this.subjects, HasCousins)) {
            for (const cp of ps.cousins()) {
                if (lookup.has(cp)) continue;
                this.subjects.push(cp);
                lookup.add(cp);
            }
        }

        this.anchors.base = this.latest?.position ?? this.initial.position;
        this.snapshots.base = Figure.fromManyAsMap(this.subjects);
        this.snapshots.reset = Maps.merge(this.snapshots.base, this.snapshots.reset);

        // Should we snap?
        if (this.grid.shouldSnap(this.subjects)) {

            this.snapping = true;

            // If so, work out the offset required to actually align the selection to the grid (assuming it's not aligned already)
            const bounds = Physicals.bounds(this.subjects);
            const actual = bounds.topLeft();
            const expected = this.grid.snap(actual);
            this.gridOffset = expected.subtract(actual);
        }
    }

    private startCloning() {
        if (this.cloning) return;
        // This is more or less the process from cloneSelection
        const tops = Physicals.topToBottom(Physicals.top(this.subjects));
        const tuples = DeqoData.wrap(tops.map(x => x.toTuple(RelativeTo.Model)));
        this.update(() => {
            // Reset any changes to the subjects
            this.applyTransform('reset', Matrix.identity);
            this.cloning = true;
            this.injector.injectNative(tuples);
            this.clones = this.session.get(Selection).toArray();
            this.anchors.clone = this.latest.position;
            this.snapshots.clone = Figure.fromManyAsMap(this.clones);
        });
    }

    private stopCloning() {
        if (!this.cloning) return;
        this.update(() => {
            this.dispatch(deleteSelection);
            this.session.apply(Selection, x => x.replace(this.subjects));
        });
        this.clones = [];
        this.snapshots.clone.clear();
        this.cloning = false;
    }

    private applyTransform(targetName: string, transform: Matrix) {
        const target = this.snapshots[targetName] as Map<Physical, Figure>;
        const elmts = Physicals.polarize(Array.from(target.keys()).flatMap(x => x.flatten(true)), Physical);
        const scope = new TransformScope(elmts, 'translate');
        for (const e of elmts) {
            // Scaling, we add in "children" with an identity translate, so
            const from = target.get(e);
            if (from) {
                e.applyTransformation(scope, from, transform.apply(from));
            }
            else {
                const f = e.createFigure();
                e.applyTransformation(scope, f, f);
            }
        }
        return elmts;
    }

    private updatePotentialDrop(input: TransformationInput, elmts: Physical[] = []) {
        // Find potential drop target
        const target = Phys
            .from(this.spatial.lookup(input.position, DropTarget))
            .whereNotIn(elmts)
            .highest()
            ?.as(DropTarget);
        // If target found, set as potential drop
        if (target) {
            this.potentialDrop = target.queryDrop(input.position, elmts);
        }
        else {
            this.potentialDrop = null;
        }
    }

    private applyPotentialDrop(elmts: Physical[]) {
        const { target, command } = this.potentialDrop ?? {};
        if (!target || !command) return false;
        const ids = elmts.map(x => x.id);
        const result = this.dispatch(this.potentialDrop.command, [target.id, ids]);
        this.potentialDrop = null;
        return result !== false;
    }

    private updateAutoLayout(input: TransformationInput, elmts: Physical[]) {
        this.potentialInsert = this.autoLayout.queryPotentialInsert(input.position, new Set(elmts));
    }

    private applyAutoLayout(elmts: Physical[]) {
        const dirty = new Set<string>();
        let didInsert = false;
        for (const e of elmts) {
            const { ownerId } = AutoLayoutEntry.get(e) ?? {};
            // If ownerId but no potential insert, must have been removal
            if (ownerId) {
                dirty.add(ownerId);
            }
        }
        this.objects.update(u => {
            u.set('key', this.key);
            if (this.potentialInsert) {
                const { container: target, at } = this.potentialInsert;
                this.autoLayout.insert(target, at, elmts);
                didInsert = true;
                dirty.delete(target.id);
                this.potentialInsert = null;
            }
            if (dirty.size) {
                for (const id of dirty) {
                    const al = this.objects.getMaybe(id, AutoLayout);
                    if (!al) continue;
                    this.autoLayout.refresh(al);
                }
            }
        });
        return didInsert;
    }

    protected findSnappingCandidatesFor(guides: GuideLine[]) {

        const ignore = Phys.from(this.cloning ? this.clones : [...this.subjects, ...this.clones])
            .flatten()
            .toSet();

        const view = this.camera.getModelFrame();
        const lines = guides.map(g => (
            (g.axis === 'x')
                ? Line.from([view.left, g.position, view.right, g.position])
                : Line.from([g.position, view.top, g.position, view.bottom])
        ));

        const extrusions = lines.map(x => Rect.fromPoints(x.extrude(10, 10)));
        const results = new Set<Physical>();

        for (const e of extrusions) {
            const elmts = this.spatial.lookup(e);
            for (const p of elmts) {
                if (ignore.has(p)) continue;
                results.add(p);
            }
        }

        return results;
    }

    private paint(gfx: UniversalSurfaceRenderer) {
        const mvm = this.camera.modelViewMatrix;
        const subjectBounds = Physicals.bounds(this.subjects);

        this.paintSnaps(gfx, mvm, subjectBounds, this.snaps);
        this.paintAutoLayout(gfx);
        this.paintDrop(gfx);
    }

    private paintAutoLayout = (gfx: UniversalSurfaceRenderer) => {
        const { container, at, guides, style } = this.potentialInsert ?? {};
        if (!container || !at || !guides) return;

        gfx.setTransform(this.camera.modelViewMatrix);
        gfx.strokeStyle = Colors.primaryLight;
        gfx.lineCap = 'round';
        gfx.lineWidth = style === 'thin' ? 3 : 6;

        for (const g of guides) {
            gfx.beginPath();
            gfx.moveTo(g.p1.x, g.p1.y);
            gfx.lineTo(g.p2.x, g.p2.y);
            gfx.stroke();
        }
    };

    private paintDrop = (gfx: UniversalSurfaceRenderer) => {
        const { guides } = this.potentialDrop ?? {};
        if (!guides) return;

        gfx.setTransform(this.camera.modelViewMatrix);
        gfx.strokeStyle = Colors.primaryLight;
        gfx.lineCap = 'round';

        for (const g of guides) {
            gfx.lineWidth = (g.style ?? 'default') === 'default' ? 3 : 6;
            gfx.beginPath();
            gfx.stroke(Polyline.from(g.points).toPath().toPath2D());
        }
    };

}
